import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	SimpleChanges,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { Jurisdiction, JurisdictionSelector } from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { BehaviorSubject, combineLatest } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";

import { ProgressBarMode } from "../progress-bar/progress-bar.model";
import { getPasswordErrors, PasswordStrengthValidityType } from "../validators/validators";

import { PasswordStrength } from "./password-strength.model";
import { PasswordStrengthService } from "./password-strength.service";

@Component({
	selector: "obg-password-strength",
	templateUrl: "./password-strength.component.html",
	styleUrls: ["./password-strength.component.scss"],
	encapsulation: ViewEncapsulation.Emulated,
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordStrengthComponent implements OnInit, OnChanges, OnDestroy {
	@Input() password: string | undefined;
	@Input() languageMessageKey = "register.password.language-message";

	@Input()
	@coerceBoolean()
	showDetails = false;

	@Input()
	@coerceBoolean()
	showLanguageMessage = false;

	@HostBinding("class.obg-password-strength") hostCssClass = true;

	passwordStrengthName: string | undefined;
	passwordStrength: number | undefined;
	passwordLengthValid: boolean | undefined;
	passwordContainsNumber: boolean | undefined;
	passwordContainsCasing: boolean | undefined;
	passwordContainsSpecialChar: boolean | undefined;
	showContainSpecialChar: boolean | undefined;
	readonly progressBarMode = ProgressBarMode.PasswordStrength;
	readonly PasswordStrength = PasswordStrength;
	private passwordChange$ = new BehaviorSubject<string>("");

	constructor(
		private store: Store<CoreContentState>,
		private service: PasswordStrengthService,
		private changeDetectorRef: ChangeDetectorRef
	) {}

	ngOnInit() {
		combineLatest([
			this.passwordChange$.asObservable(),
			this.store.pipe(select(JurisdictionSelector.get), ignoreNil()),
		])
			.pipe(debounceTime(200), distinctUntilChanged())
			// TODO: AC-3839
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(([password, jurisdiction]) => {
				this.passwordStrength = this.service.measurePasswordStrength(password);
				if (jurisdiction === Jurisdiction.Aams) {
					this.showContainSpecialChar = true;
					if (this.passwordStrength === 2) {
						this.passwordStrength = this.passwordStrength - 1;
					}
				}
				this.passwordStrengthName = PasswordStrength[this.passwordStrength]?.toLowerCase();
				this.updatePasswordValidation(getPasswordErrors(password, false, jurisdiction));
				this.changeDetectorRef.markForCheck();
			});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.password) {
			this.passwordChange$.next(this.password || "");
		}
	}

	ngOnDestroy() {
		this.passwordChange$.complete();
	}

	private updatePasswordValidation(passwordErrors: PasswordStrengthValidityType) {
		if (passwordErrors) {
			this.passwordLengthValid =
				passwordErrors.passwordMinimumLength === undefined &&
				passwordErrors.passwordMaximumLength === undefined;
			this.passwordContainsNumber = passwordErrors.passwordNumber === undefined;
			this.passwordContainsCasing =
				passwordErrors.passwordUppercase === undefined && passwordErrors.passwordLowercase === undefined;
			this.passwordContainsSpecialChar = passwordErrors.passwordSpecialChar === undefined;
			return;
		}
		this.passwordLengthValid = true;
		this.passwordContainsNumber = true;
		this.passwordContainsCasing = true;
		this.passwordContainsSpecialChar = true;
	}
}
